import { toDate, parseISO } from 'date-fns';
import * as React from 'react';
import classnames from 'classnames';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';

import {
	useMonthsPricesMap,
	useCurrentMonth,
	useCenteredItem,
	useCenteredDayIndex,
	useSliderHandlers,
	useMonthNavigation,
	useSelectedPrice,
	getMonthsArray,
	safeStartOfMonth,
	PriceGraphProps,
	RouteType,
	Legends,
	Month,
	MonthsList
} from '@features/price-graph';
import { CdnIcon } from '@shared/ui/CdnIcon';
import { Money } from '../../../../Money';
import Slider from '../../../../Slider';
import { format, removeDotsFromDate, TABLET_MIN_WIDTH } from '../../../../utils';
import { ThemeProvider, useTheme } from '../../../../theme';
import { default as BaseButton } from '../../../../BaseComponents/Button';

const SLIDER_STEP = 5;

const PriceGraph: React.FC<PriceGraphProps> = ({
	openDate,
	onMonthChange,
	backDate,
	outDate,
	onPricesEnd,
	onSelect,
	prices,
	routeType,
	departureCity,
	arrivalCity
}) => {
	const { t } = useTranslation('PriceGraph');
	const { PriceGraphStyles: css, SliderStyles: sliderStyles } = useTheme('Features').PriceGraph;

	const [openedDate, setOpenedDate] = React.useState<Date>(openDate);

	const monthsPricesMap = useMonthsPricesMap(prices);

	const { currentMonthISO: currentMonth, isMonthExist } = useCurrentMonth(openedDate, monthsPricesMap);

	const monthsArray = React.useMemo(() => getMonthsArray(monthsPricesMap), [monthsPricesMap]);
	const minDate = safeStartOfMonth(monthsArray[0].dateISO);

	const centeredItem = useCenteredItem(openDate, openedDate, minDate);

	const getCenteredDayIndex = useCenteredDayIndex({ openedDate, backDate, outDate, openDate });

	const { onItemCentered, sliderArrowHandler, mobileArrowHandler } = useSliderHandlers({
		currentMonthISO: currentMonth,
		isMonthExist,
		onMonthChange,
		onPricesEnd,
		setOpenedDate
	});

	const { isNextMonthExists, isPrevMonthExists } = useMonthNavigation(currentMonth, isMonthExist);

	const selectedPrice = useSelectedPrice(prices, openedDate);

	const maxPriceOfMonth = monthsPricesMap[currentMonth]?.maxPrice ?? { amount: 0, currency: null };
	const minPriceOfMonth = monthsPricesMap[currentMonth]?.minPrice ?? { amount: 0, currency: null };

	const onSelectChange = (value: string) => {
		onMonthChange && onMonthChange(parseISO(value));
		setOpenedDate(parseISO(value));
	};

	const onSelectDate = () => {
		onSelect(openedDate, routeType === RouteType.Return);
	};

	const routeDirection =
		routeType === RouteType.Return ? `${arrivalCity} — ${departureCity}` : `${departureCity} — ${arrivalCity}`;

	return (
		<div className={css.container}>
			<div className={css.header}>
				{(departureCity || arrivalCity) && <div className={css.routeType}>{routeDirection}</div>}

				<MonthsList months={monthsArray} selectedMonth={currentMonth} onChange={onSelectChange} />
			</div>

			<MediaQuery maxWidth={+TABLET_MIN_WIDTH + 1}>
				<div className={css.graphHeader}>
					<div className={css.graphHeader__dates}>
						<CdnIcon icon={'Flight/Direction'} />
						<div
							className={classnames(css.graphHeader__outDate, {
								[css.graphHeader__date_notActive]: routeType === RouteType.Return
							})}
						>
							{!outDate && removeDotsFromDate(format(toDate(openedDate), 'd MMM, EEEEEE'))}
							{outDate &&
								removeDotsFromDate(
									format(
										toDate(routeType === RouteType.Return ? outDate : openedDate),
										'd MMM, EEEEEE'
									)
								)}
						</div>

						{outDate && (
							<div
								className={classnames(css.graphHeader__backDate, {
									[css.graphHeader__date_notActive]: routeType === RouteType.Outbound
								})}
							>
								{backDate &&
									removeDotsFromDate(
										format(
											toDate(routeType === RouteType.Outbound ? backDate : openedDate),
											'd MMM, EEEEEE'
										)
									)}
							</div>
						)}
						<div className={css.graphHeader__priceWrapper}>
							{t('from')} {selectedPrice?.price ? <Money money={selectedPrice.price} /> : null}
						</div>
					</div>
				</div>
			</MediaQuery>

			<div className={css.calendar}>
				<div className={css.daysWrapper}>
					<Legends maxPrice={maxPriceOfMonth} minPrice={minPriceOfMonth}>
						{(ref: React.Ref<HTMLDivElement>) => (
							<ThemeProvider value={{ Slider: { SliderTheme: sliderStyles } }}>
								<MediaQuery minWidth={TABLET_MIN_WIDTH + 1}>
									<Slider
										innerRef={ref}
										offset={SLIDER_STEP}
										wrapperClassName={css.days}
										centeredItem={centeredItem}
										onRangeChange={sliderArrowHandler}
										hideDisabledLeftArrow={!isPrevMonthExists()}
										hideDisabledRightArrow={!isNextMonthExists()}
									>
										<Month
											initialDate={parseISO(currentMonth)}
											routeType={routeType ?? RouteType.Outbound}
											onSelect={onSelect}
											backDate={backDate}
											outDate={outDate}
											monthsPricesMap={monthsPricesMap}
											prices={prices}
										/>
									</Slider>
								</MediaQuery>

								<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
									<Slider
										innerRef={ref}
										offset={SLIDER_STEP}
										wrapperClassName={css.days}
										leftStartItem={getCenteredDayIndex(minDate)}
										onItemCentered={(startItemIndex: number) =>
											onItemCentered(startItemIndex, minDate)
										}
										onRangeChange={(_direction, startItemIndex) =>
											mobileArrowHandler(startItemIndex, minDate)
										}
										touchEvents={true}
										touchTransition="by-items"
										minItemWidth={17}
										useRealItemStep={true}
									>
										{monthsArray.map((date, index) => (
											<Month
												key={index}
												initialDate={parseISO(date.dateISO)}
												routeType={routeType}
												onSelect={setOpenedDate}
												backDate={backDate}
												outDate={outDate}
												monthsPricesMap={monthsPricesMap}
												prices={prices}
												focusedDate={openedDate.toISOString()}
												isMobileMode={true}
											/>
										))}
									</Slider>
								</MediaQuery>
							</ThemeProvider>
						)}
					</Legends>
				</div>
			</div>

			{selectedPrice && (
				<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
					<div className={css.footer}>
						<BaseButton variant="smallPrimary" className={css.button} onClick={onSelectDate}>
							{t('Show flights')}
						</BaseButton>
					</div>
				</MediaQuery>
			)}
		</div>
	);
};

export default PriceGraph;
